import { apiDelete, apiGet, apiPost, apiPut } from '../../generic/Api_Functions'
import SmartMateriaUITable from '../../generic/SmartMateriaUITable'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { systemValueDescription, systemValueLabel } from '../../generic/systemValues/systemValueLabel'

// data_type has no code column and its names (Text, Integer…) are too common to be keys: the seeded name in capitals
// is its code (TEXT, INTEGER…), the casing of the name is kept (P17).
const dataTypeValue = (name) => ({ code: (name || '').toUpperCase(), name: name || '' })

const ToolTypeParameter = () => {
  const { t_ } = useSmartTranslation()

  const columns = [{
    name: t_('Code'),
    field: 'code',
    charLimit: 60,
    mandatory: true,
    unique: true,
    readOnly: true
  },
  {
    name: t_('Name'),
    field: 'name',
    charLimit: 60,
    mandatory: true,
    unique: true,
    // Tool parameter codes are abbreviations (A, F, R, z…), too short to be keys: a seeded parameter is keyed by its trimmed name (P17, as Sun's catalogue declares it).
    render: (row) => systemValueLabel(t_, 'tool_parameter', { code: (row.name || '').trim(), name: row.name, systemDefault: row.systemDefault }),
    renderOnlyInReadMode: true,
    readOnly: (row) => {
      return row.systemDefault
    }
  },
  {
    name: t_('Description'),
    field: 'description',
    charLimit: 80,
    render: (row) => systemValueDescription(t_, 'tool_parameter', { code: (row.name || '').trim(), name: row.name, description: row.description, systemDefault: row.systemDefault }),
    renderOnlyInReadMode: true
  },
  {
    name: t_('Type'),
    field: 'toolParameterType.code',
    type: 'select',
    edit: {
      source: async () => {
        return await apiGet('toolParameterTypes')
      },
      id: 'id',
      field: 'code'
    },
    mandatory: true,
    readOnly: (row) => {
      if (row.systemDefault === undefined || row.systemDefault === null) {
        return false
      }
      return row.systemDefault
    }
  },
  {
    name: t_('Data Type'),
    field: 'dataType.name',
    type: 'select',
    render: (row) => systemValueLabel(t_, 'data_type', dataTypeValue(row.dataType?.name)),
    renderOnlyInReadMode: true,
    filterByLabel: { options: (items) => items.map((item) => ({ value: item.name, label: systemValueLabel(t_, 'data_type', dataTypeValue(item.name)) })) },
    edit: {
      source: async () => {
        return await apiGet('dataTypes')
      },
      id: 'id',
      field: 'name',
      optionLabel: (name) => systemValueLabel(t_, 'data_type', dataTypeValue(name))
    },
    mandatory: true,
    readOnly: (row) => {
      return row.systemDefault
    }
  },
  {
    name: t_('Unit type'),
    field: 'unitType.code',
    type: 'select',
    edit: {
      source: async () => {
        return await apiGet('unitTypes')
      },
      id: 'id',
      field: 'code'
    },
    mandatory: false,
    readOnly: (row) => {
      return row.systemDefault
    }
  }
  ]

  const actions = {
    edit: {
      fetchFunction: async (row) => {
        const edit = await apiPut('toolParameters', row)
        return edit
      }
    },
    create: {
      fetchFunction: async (row) => {
        const newParameter = Object.assign(row)
        if (newParameter.unitType && !newParameter.unitType.id) {
          delete newParameter.unitType
        }
        const create = await apiPost('toolParameters', newParameter)
        return create
      }
    },
    delete: {
      visibilityButton: toolParameter => !toolParameter.systemDefault,
      fetchFunction: async (rowsPagination) => {
        let paginationRequest = rowsPagination
        if (rowsPagination.length === 1) {
          if (rowsPagination[0].systemDefault) {
            return new Promise((resolve, reject) => reject(new Error({
              message: 'This tool parameter can`t be deleted',
              name: 'warning'
            })))
          }
          paginationRequest = {
            columns: columns.map(col => col.field),
            genericSearch: '',
            columnFilters: [],
            columnFilterValues: [],
            page: 0,
            size: -1,
            sortKey: [],
            sortMode: [],
            rows: [rowsPagination[0].id.toString()],
            primaryKeys: ['id'],
            allSelected: false
          }
        }

        const del = await apiDelete('toolParametersPage', paginationRequest)
        return del
      }
    }
  }

  return (
    <>
      <SmartMateriaUITable
        tableTuneUniqueKey='toolTypeParameter'
        columns={columns}
        title={t_('Tool Parameters')}
        dataFetch='toolParametersPage'
        multipleSelection
        actions={actions}
        pageable
      />
    </>
  )
}

export default ToolTypeParameter
